<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import ConfirmModal from '@/components/common/layout/ConfirmModal.vue'
import {
  useTemplateMailAdminStore,
  type TemplateMailItem,
} from '@/stores/useTemplateMailAdminStore'
import { PlusIcon, TrashIcon, EnvelopeIcon } from '@heroicons/vue/24/outline'

const store = useTemplateMailAdminStore()

const search = ref('')
const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  if (!q) return store.templates
  return store.templates.filter(
    (t) =>
      t.code.toLowerCase().includes(q) ||
      t.description.toLowerCase().includes(q) ||
      t.externalReference.toLowerCase().includes(q),
  )
})

const showFormModal = ref(false)
const editing = ref<TemplateMailItem | null>(null)
const form = ref({ code: '', externalReference: '', description: '' })
const saving = ref(false)
const errorMsg = ref<string | null>(null)

const showDeleteModal = ref(false)
const toDelete = ref<TemplateMailItem | null>(null)
const deleteError = ref<string | null>(null)

function openCreate() {
  editing.value = null
  form.value = { code: '', externalReference: '', description: '' }
  errorMsg.value = null
  showFormModal.value = true
}

function openEdit(t: TemplateMailItem) {
  editing.value = t
  form.value = { code: t.code, externalReference: t.externalReference, description: t.description }
  errorMsg.value = null
  showFormModal.value = true
}

function closeFormModal() {
  showFormModal.value = false
  editing.value = null
  errorMsg.value = null
}

async function submit() {
  if (!form.value.code.trim() || !form.value.externalReference.trim() || !form.value.description.trim()) {
    errorMsg.value = 'Tous les champs sont obligatoires'
    return
  }
  saving.value = true
  errorMsg.value = null
  try {
    const payload = {
      code: form.value.code.trim().toUpperCase(),
      externalReference: form.value.externalReference.trim(),
      description: form.value.description.trim(),
    }
    const res = editing.value
      ? await store.update(editing.value.uuid, payload)
      : await store.create(payload)
    if (res.ok) closeFormModal()
    else errorMsg.value = res.message
  } finally {
    saving.value = false
  }
}

function askDelete(t: TemplateMailItem) {
  toDelete.value = t
  deleteError.value = null
  showDeleteModal.value = true
}

async function confirmDelete() {
  if (!toDelete.value) return
  const res = await store.remove(toDelete.value.uuid)
  if (res.ok) {
    showDeleteModal.value = false
    toDelete.value = null
  } else {
    deleteError.value = res.message
  }
}

function formatDate(s: string | null | undefined) {
  if (!s) return '—'
  return new Date(s).toLocaleString('fr-FR')
}

onMounted(async () => {
  await store.fetchAll()
})
</script>

<template>
  <div class="p-6">
    <Bs2Title title="Templates Brevo" subtitle="SUPER_ADMIN uniquement" />

    <div class="flex flex-wrap gap-2 mt-4">
      <router-link to="/dashboard/admin" class="btn btn-outline btn-sm">← Retour dashboard admin</router-link>
    </div>

    <div class="mt-8 card bg-base-100 shadow">
      <div class="card-body">
        <div class="flex items-center justify-between mb-4 gap-2">
          <input
            v-model="search"
            type="text"
            placeholder="Rechercher (code, description, id Brevo)…"
            class="input input-bordered input-sm w-72"
          />
          <div class="flex gap-2">
            <ButtonDaisy text="Actualiser" variant="outline" size="sm" @click="store.fetchAll()" />
            <button class="btn btn-primary btn-sm gap-1" @click="openCreate">
              <PlusIcon class="w-4 h-4" />
              Nouveau template
            </button>
          </div>
        </div>

        <div v-if="store.isLoading" class="py-8 text-center">
          <span class="loading loading-spinner loading-lg" />
        </div>
        <div v-else-if="filtered.length === 0" class="py-8 text-center text-base-content/60">
          Aucun template.
        </div>
        <div v-else class="overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Code interne</th>
                <th>ID Brevo</th>
                <th>Description</th>
                <th>Mis à jour</th>
                <th class="w-32"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="t in filtered" :key="t.uuid" class="hover">
                <td class="font-mono font-semibold">{{ t.code }}</td>
                <td class="font-mono">{{ t.externalReference }}</td>
                <td class="text-sm">{{ t.description }}</td>
                <td class="text-xs text-base-content/60">{{ formatDate(t.dateUpdated) }}</td>
                <td>
                  <div class="flex gap-1 justify-end">
                    <button class="btn btn-ghost btn-xs" @click="openEdit(t)">Modifier</button>
                    <button
                      class="btn btn-ghost btn-xs text-error"
                      title="Supprimer"
                      @click="askDelete(t)"
                    >
                      <TrashIcon class="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="modal" :class="{ 'modal-open': showFormModal }">
      <div class="modal-box max-w-lg">
        <h3 class="font-bold text-lg flex items-center gap-2">
          <EnvelopeIcon class="w-6 h-6" />
          {{ editing ? `Modifier ${editing.code}` : 'Nouveau template Brevo' }}
        </h3>
        <div class="mt-4 space-y-4">
          <div class="form-control">
            <label class="label"><span class="label-text">Code interne</span></label>
            <input
              v-model="form.code"
              type="text"
              class="input input-bordered input-sm font-mono"
              placeholder="WELCOME_EMAIL"
              :disabled="saving"
              @input="form.code = form.code.toUpperCase().replace(/[^A-Z0-9_]/g, '_')"
            />
          </div>
          <div class="form-control">
            <label class="label"><span class="label-text">ID template Brevo</span></label>
            <input
              v-model="form.externalReference"
              type="text"
              class="input input-bordered input-sm font-mono"
              placeholder="123"
              :disabled="saving"
            />
          </div>
          <div class="form-control">
            <label class="label"><span class="label-text">Description</span></label>
            <textarea
              v-model="form.description"
              class="textarea textarea-bordered textarea-sm"
              rows="2"
              :disabled="saving"
            />
          </div>
          <div v-if="errorMsg" class="alert alert-error text-sm">{{ errorMsg }}</div>
        </div>
        <div class="modal-action">
          <button class="btn" :disabled="saving" @click="closeFormModal">Annuler</button>
          <button class="btn btn-primary" :disabled="saving" @click="submit">
            {{ saving ? 'Enregistrement…' : (editing ? 'Enregistrer' : 'Créer') }}
          </button>
        </div>
      </div>
      <div class="modal-backdrop" @click="closeFormModal" />
    </div>

    <ConfirmModal
      :show-modal="showDeleteModal"
      title="Supprimer le template"
      :body="`Supprimer « ${toDelete?.code ?? ''} » ?`"
      confirm-text="Supprimer"
      confirm-variant="error"
      @confirm="confirmDelete"
      @cancel="showDeleteModal = false; toDelete = null; deleteError = null"
    />
    <div v-if="deleteError" class="toast toast-end">
      <div class="alert alert-error text-sm">{{ deleteError }}</div>
    </div>
  </div>
</template>
